// ============================================================
// THE ATTACK'S BEAT FACTS - who struck, how, and whether anything went off
// (plan map-display, Track D).
//
// Every attack entry - a hit, a miss, a jam - carries them in its own `beat`
// column, a JSON string (text, never json: the log's echo prevention compares
// strings, CLAUDE.md "Adding a synced collection"). The shared log keeps only
// its declared columns, so neither `result` nor `totalDamage` reaches another
// screen: this is how a remote screen knows a miss was a miss. The acting
// combatant's id is veiled at the sync boundary like any id (hooks/useVeil),
// so a hidden attacker reaches a player as its alias.
//
// Pure. Zero React dependency.
// ============================================================

import { finite } from './battlefield/facing';
import { SIGNATURES, type Signature } from './stealth/noise';

/** How the attack came out, as far as a beat cares: it reached the target, missed, or never went off. */
export type BeatOutcome = 'landed' | 'miss' | 'jam';

/**
 * WHAT KIND OF ATTACK it was (plan design-gaps, K1): the action it was made under, so the display can play a
 * charge's rush, an all-out throw, a guarded sweep, the swift and lightning bars, an overwatch interrupt. Standard
 * is the plain strike or shot, and the column leaves it unsaid.
 */
export type AttackKind =
  | 'standard'
  | 'charge'
  | 'allOut'
  | 'guarded'
  | 'swift'
  | 'lightning'
  | 'overwatch'
  /** The body ram (K5): Knock-Down, Stun, Grapple, and a vehicle's ram through its stand-in weapon. */
  | 'ram'
  /** A Feint (K3): the swing that stops short. */
  | 'feint'
  /** A horde the GM split in two, or broke to flight (V4): no attack, the horde's own beat. */
  | 'split'
  | 'break'
  /** Stealth's quiet moves (Z1): the gesture on the map, never the roll's result. */
  | QuietKind;

/** The quiet moves' kinds (Z1): each plays its own shape on the display, and lands nothing. */
export const QUIET_KINDS = ['observe', 'moveUnseen', 'hide', 'distract', 'shadow'] as const;
export type QuietKind = (typeof QUIET_KINDS)[number];
/** The rate of fire a shot was made at; null for a melee attack. Suppressing fire is its own. */
export type RateOfFire = 'single' | 'semi' | 'full' | 'suppress';

const KINDS: readonly AttackKind[] = [
  'standard',
  'charge',
  'allOut',
  'guarded',
  'swift',
  'lightning',
  'overwatch',
  'ram',
  'feint',
  'split',
  'break',
  ...QUIET_KINDS,
];
const RATES: readonly RateOfFire[] = ['single', 'semi', 'full', 'suppress'];

/** The facts the kind is read from: what the attack flow knows at the moment it writes the line. */
export interface AttackKindFacts {
  melee: boolean;
  /** The EFFECTIVE fire mode (engine/attackCalc `hitMode`): a melee semi is `swift`, a melee full `lightning`. */
  fireMode: string;
  charge?: boolean;
  allOut?: boolean;
  guarded?: boolean;
  /** Fired from an overwatch zone (plan design-gaps, K5). */
  overwatch?: boolean;
  suppressing?: boolean;
  /** A vehicle's ram (attack/ram `rammingContext`). */
  ram?: boolean;
}

/**
 * The one reading of the kind, so the three writers cannot drift: a charge over an all-out attack over a guarded
 * one (the toggles exclude each other, but the order is the rules' weight), then a melee's own rate (swift,
 * lightning), an overwatch shot, else standard.
 */
export function attackKindOf(f: AttackKindFacts): AttackKind {
  if (f.ram) return 'ram';
  if (f.charge) return 'charge';
  if (f.allOut) return 'allOut';
  if (f.guarded) return 'guarded';
  if (f.melee) return meleeRateKind(f.fireMode);
  return f.overwatch ? 'overwatch' : 'standard';
}

/** A melee attack's rate as its kind: the EFFECTIVE mode (`hitMode` maps a melee semi to swift, full to lightning). */
function meleeRateKind(fireMode: string): AttackKind {
  if (fireMode === 'swift') return 'swift';
  return fireMode === 'lightning' ? 'lightning' : 'standard';
}

/** The rate of fire a shot was made at: suppressing fire first, then the mode; a melee attack has none. */
export function rateOfFireOf(f: AttackKindFacts): RateOfFire | null {
  if (f.melee) return null;
  if (f.suppressing) return 'suppress';
  return f.fireMode === 'semi' || f.fireMode === 'full' ? f.fireMode : 'single';
}

export interface AttackBeat {
  actorId: string | null;
  melee: boolean;
  outcome: BeatOutcome;
  /** Who was struck, by id: the shared log matches by it, not by a name two figures may share. */
  targetId: string | null;
  /**
   * When the line was logged, on the screen that logged it. The shared copy is stamped again (strictly
   * increasing per writer), so this is what makes the two copies one attack: impacts key on it.
   */
  at: number | null;
  /** A reveal's line (emergeBeat), not an attack's: the figure out of hiding. */
  emerge: boolean;
  /** The weapon's noise (engine/stealth/noise), for the display's ring in Stealth; null when it went unsaid. */
  noise: 'loud' | 'silent' | null;
  /**
   * The weapon's Signature step (plan armoury, stage 2): how far its ring spreads and how far a wedge turns to it,
   * at the table's reach. Beside `noise`, never instead: a screen from before the ladder reads `noise` alone.
   */
  signature: Signature | null;
  /**
   * The weapon's special rules (plan body-map, R4), so a plate and the map can play their accents on every screen:
   * the shared log keeps no slot. A veiled attacker's specials show as its weapon's name already does.
   */
  specials: string[];
  /**
   * Specials' LOOKS a holding weapon rule lent the swing (plan armoury): played like specials by the display,
   * NEVER among `specials`, which mechanics read by rule id (Daemonic's bypass). Empty when none.
   */
  looks: string[];
  /** The action it was made under (plan design-gaps, K1); `standard` when the column says nothing. */
  kind: AttackKind;
  /** The rate of fire, for a shot; null for a melee attack or a column that says nothing. */
  rof: RateOfFire | null;
  /** The cover's AP the hits met, the most of any hit; 0 when none or unsaid. "COVER · AP n" on the display. */
  coverAp: number;
  /** A body action's line (actionBeat), not a weapon's: the catalog row's id; null on an attack's. */
  action: string | null;
  /** A blast's template this victim was caught in (plan design-gaps, T2); null for anything else. */
  area: AreaBurst | null;
}

/**
 * A blast template as one victim's beat carries it (plan design-gaps, T2): its centre on the ground and radius (m);
 * where the throw was aimed when it scattered (`from`, null when it did not); a spawned copy's step (`copy`: 0 the
 * primary, 1+ a copy, set off from its spawner after the primary); and `late`, a copy whose primary scattered, so it
 * waits for that slide too.
 */
export interface AreaBurst {
  x: number;
  z: number;
  r: number;
  from: { x: number; z: number } | null;
  copy: number;
  late: boolean;
}

/** What a writer hands: the scatter and the copy left out when there is none. */
export type AreaBurstInput = Pick<AreaBurst, 'x' | 'z' | 'r'> & {
  from?: { x: number; z: number };
  copy?: number;
  late?: boolean;
};

const OUTCOMES: readonly BeatOutcome[] = ['landed', 'miss', 'jam'];

/** The column's value for an attack entry. */
export function attackBeat(
  actorId: string | null | undefined,
  melee: boolean,
  outcome: BeatOutcome,
  targetId?: string | null,
  at?: number | null,
  options: AttackBeatOptions = {},
): string {
  return JSON.stringify({
    actorId: actorId ?? null,
    melee,
    outcome,
    targetId: targetId ?? null,
    at: at ?? null,
    ...soundOf(options),
    ...weaponFacts(options),
  });
}

/** The weapon's sound: the coarse word, and the step only where that word cannot say it (muffled, deafening). */
function soundOf({ noise, signature }: AttackBeatOptions) {
  return {
    ...(noise ? { noise } : {}),
    ...(signature === 'muffled' || signature === 'deafening' ? { signature } : {}),
  };
}

/** What else the column says of the attack, each only when there is something to say. */
function weaponFacts({ specials, looks, kind, rof, coverAp, action, area }: AttackBeatOptions) {
  return {
    ...(specials?.length ? { specials } : {}),
    ...(looks?.length ? { looks } : {}),
    // The plain case is left unsaid: the column stays short on every ordinary line.
    ...(kind && kind !== 'standard' ? { kind } : {}),
    ...(rof ? { rof } : {}),
    ...(coverAp ? { coverAp } : {}),
    ...(action ? { action } : {}),
    ...(area ? { area: writeArea(area) } : {}),
  };
}

/** A centimetre is all the map needs; the column stays short. */
const cm = (n: number) => Math.round(n * 100) / 100;

/** The template as the column stores it: rounded, the scatter and the copy only when there are. */
function writeArea({ x, z, r, from, copy, late }: AreaBurstInput) {
  return {
    x: cm(x),
    z: cm(z),
    r: cm(r),
    ...(from ? { from: { x: cm(from.x), z: cm(from.z) } } : {}),
    ...(copy ? { copy } : {}),
    ...(late ? { late } : {}),
  };
}

export interface AttackBeatOptions {
  /** A body action's catalog id (actionBeat). */
  action?: string | null;
  noise?: 'loud' | 'silent' | null;
  signature?: Signature | null;
  specials?: readonly string[] | null;
  looks?: readonly string[] | null;
  kind?: AttackKind | null;
  rof?: RateOfFire | null;
  coverAp?: number | null;
  /** A blast's template, for each victim it caught (T2). */
  area?: AreaBurstInput | null;
}

/**
 * A body action's column (plan design-gaps, K2): Knock-Down, Stun, Feint or Grapple resolved on the actor's
 * target - the attack's column with the catalog row's id, played as its row says (`ActionDef.beat.plays`), the
 * outcome landed when it won and missed when it did not. Melee by nature.
 */
export function actionBeat(
  actorId: string | null | undefined,
  targetId: string,
  action: { id: string; plays: AttackKind },
  won: boolean,
  at: number,
): string {
  return attackBeat(actorId, true, won ? 'landed' : 'miss', targetId, at, { kind: action.plays, action: action.id });
}

/**
 * A horde split in two or broken to flight (plan design-gaps, V4): the GM tool's story line carries this in the
 * same column, the horde both the actor and the one it happens to, so every screen plays it on the map.
 */
export const hordeBeat = (hordeId: string, kind: 'split' | 'break', at: number): string =>
  actionBeat(hordeId, hordeId, { id: kind, plays: kind }, true, at);

/**
 * A hidden figure's reveal (plan map-display, G2): its "emerges" line carries
 * this in the same column, with the figure's id, so every screen - on either
 * renderer - plays the figure's arrival out of hiding (engine/impacts reads it
 * as an `emerge`).
 */
export const emergeBeat = (targetId: string, at: number): string => JSON.stringify({ emerge: true, targetId, at });

/** A weapon's noise off the column: loud or silent, anything else unsaid. */
const readNoise = (v: unknown): AttackBeat['noise'] => (v === 'loud' || v === 'silent' ? v : null);

/** The step off the column; absent, the coarse word's own step (an older line, or a plain silent or loud one). */
function readSignature(v: unknown, noise: AttackBeat['noise']): Signature | null {
  if (SIGNATURES.includes(v as Signature)) return v as Signature;
  return noise;
}

/** The kind off the column: one of the kinds, else standard (an older line, or nonsense). */
/** One of `list`, else `or`: every enumerated field of the column reads through this. */
function oneOf<T extends string, O>(list: readonly T[], v: unknown, or: O): T | O {
  return list.includes(v as T) ? (v as T) : or;
}

const readKind = (v: unknown): AttackKind => oneOf(KINDS, v, 'standard');
/** The rate of fire off the column: one of the rates, else none. */
const readRate = (v: unknown): RateOfFire | null => oneOf(RATES, v, null);
/** The cover's AP off the column: a positive number, else 0. */
const readCoverAp = (v: unknown): number => (typeof v === 'number' && v > 0 ? v : 0);

/** The body action's id off the column: a string, else none (a weapon's line). */
const readAction = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

const readPoint = (v: unknown): { x: number; z: number } | null => {
  const p = v as { x?: unknown; z?: unknown } | null;
  return p && finite(p.x) && finite(p.z) ? { x: p.x, z: p.z } : null;
};

/** The template off the column: a centre and a positive radius, else none. */
function readArea(v: unknown): AreaBurst | null {
  const a = v as { r?: unknown; from?: unknown; copy?: unknown; late?: unknown } | null;
  const centre = readPoint(v);
  if (!centre || !a || !finite(a.r) || a.r <= 0) return null;
  const copy = finite(a.copy) && a.copy > 0 ? a.copy : 0;
  return { ...centre, r: a.r, from: readPoint(a.from), copy, late: a.late === true };
}

/** The specials off the column: its strings, nothing else. */
const readSpecials = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((s): s is string => typeof s === 'string') : [];

/** The column parsed, an object or nothing: every reader of a beat (an attack's, a move's) starts here. */
export function parseBeat(value: unknown): Record<string, unknown> | null {
  if (typeof value !== 'string') return null;
  let v: unknown;
  try {
    v = JSON.parse(value);
  } catch {
    return null;
  }
  return v && typeof v === 'object' ? (v as Record<string, unknown>) : null;
}

/**
 * The facts off an entry's column, an attack's or a reveal's (`emerge`) - parsed ONCE, by this alone; null when
 * it has none, or another reader's (a move's, engine/moveBeat).
 */
export function readAttackBeat(value: unknown): AttackBeat | null {
  const v = parseBeat(value);
  if (!v || v.move === true) return null;
  const {
    actorId,
    melee,
    outcome,
    targetId,
    at,
    emerge,
    noise,
    signature,
    specials,
    kind,
    rof,
    coverAp,
    action,
    area,
  } = v;
  return {
    noise: readNoise(noise),
    signature: readSignature(signature, readNoise(noise)),
    specials: readSpecials(specials),
    looks: readSpecials(v.looks),
    kind: readKind(kind),
    rof: readRate(rof),
    coverAp: readCoverAp(coverAp),
    action: readAction(action),
    area: readArea(area),
    emerge: emerge === true,
    actorId: typeof actorId === 'string' ? actorId : null,
    targetId: typeof targetId === 'string' ? targetId : null,
    at: typeof at === 'number' ? at : null,
    melee: melee === true,
    outcome: oneOf(OUTCOMES, outcome, 'landed'),
  };
}

/**
 * Who a line struck, by id, among `people`: the id its column names - and ONLY that id when it names one (a
 * figure of the same name is not the one meant) - else, an older line, the first of its target's name. The app
 * (engine/impacts) and the fuzzer's beat oracle (engine/fuzz/snapshot) both ask this, so they cannot drift.
 */
export function struckId(
  beat: AttackBeat | null,
  targetName: string | undefined,
  people: readonly { id: string; name: string }[],
): string | undefined {
  if (beat?.targetId) return people.some((p) => p.id === beat.targetId) ? beat.targetId : undefined;
  return targetName ? people.find((p) => p.name === targetName)?.id : undefined;
}
